import { useEffect, useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { diaIso, moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import type { ContaFinanceira, MetodoDePagamento, Titulo } from '@/data/tipos';
import { centavos } from '@/screens/vendas/NovoOrcamentoModal';

export interface BaixaModalProps {
    titulo: Titulo | null;
    contas: ContaFinanceira[];
    metodos: MetodoDePagamento[];
    onClose: () => void;
    onRegistrado: () => void;
}

export function BaixaModal({ titulo, contas, metodos, onClose, onRegistrado }: BaixaModalProps) {
    const aviso = useAviso();
    const idValor = useId();
    const idData = useId();
    const idConta = useId();
    const idMetodo = useId();
    const [valor, setValor] = useState('');
    const [pagoEm, setPagoEm] = useState(diaIso());
    const [contaId, setContaId] = useState('');
    const [metodoId, setMetodoId] = useState('');
    /* A baixa já nasce com o valor que falta: "tem valor" não é "mexeu". */
    const formSujo = useAlteracaoPendente(
        { valor, pagoEm, contaId, metodoId },
        titulo && `${titulo.id}:${contas.length}:${metodos.length}`,
    );
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    const falta = titulo ? titulo.valorCentavos - titulo.pagoCentavos : 0;

    useEffect(() => {
        if (!titulo) return;
        /* O que falta já vem preenchido: a baixa cheia é o caso comum, e digitar
           o valor de novo é onde nasce o erro de centavo. */
        setValor((falta / 100).toFixed(2).replace('.', ','));
        setPagoEm(diaIso());
        setContaId(contas[0]?.id ?? '');
        setMetodoId(metodos[0]?.id ?? '');
        setErro(null);
    }, [titulo, falta, contas, metodos]);

    if (!titulo) return null;

    const metodo = metodos.find((m) => m.id === metodoId);
    const valorCentavos = centavos(valor);
    const taxa = Math.round(valorCentavos * ((metodo?.taxaPercentual ?? 0) / 100));
    const ehReceber = titulo.tipo === 'receber';

    const registrar = async () => {
        setSalvando(true);
        try {
            await dados().registrarPagamento({
                tituloId: titulo.id,
                valorCentavos,
                pagoEm,
                contaId: contaId || undefined,
                metodoId: metodoId || undefined,
            });
            aviso({
                title: ehReceber ? 'Recebimento registrado' : 'Pagamento registrado',
                description: `${titulo.descricao} · ${moeda(valorCentavos)}`,
            });
            onRegistrado();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível registrar.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={520}
            title={ehReceber ? 'Registrar recebimento' : 'Registrar pagamento'}
            description={`${titulo.descricao} · ${titulo.contraparte}`}
            dirty={formSujo}
            onSave={() => void registrar()}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void registrar()} loading={salvando}>Registrar</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <FormField label="Valor" required htmlFor={idValor} hint={`Falta ${moeda(falta)}`}>
                        <Input id={idValor} value={valor} onChange={(v) => { setValor(v); setErro(null); }} invalid={!!erro} />
                    </FormField>
                    <FormField label={ehReceber ? 'Recebido em' : 'Pago em'} required htmlFor={idData}>
                        <Input id={idData} type="date" value={pagoEm} onChange={setPagoEm} />
                    </FormField>
                    <FormField label="Conta" htmlFor={idConta}>
                        <Select id={idConta} value={contaId} onChange={setContaId} options={contas.map((c) => ({ value: c.id, label: c.nome }))} />
                    </FormField>
                    <FormField label="Forma de pagamento" htmlFor={idMetodo}>
                        <Select id={idMetodo} value={metodoId} onChange={setMetodoId} options={metodos.map((m) => ({ value: m.id, label: m.nome }))} />
                    </FormField>
                </div>

                {/* Taxa e prazo aparecem antes de confirmar: "recebi R$ 100" no
                    crédito vira R$ 96,71 na conta trinta dias depois, e é isso
                    que faz o extrato não fechar quando ninguém avisa. */}
                {metodo && (metodo.taxaPercentual > 0 || metodo.prazoDias > 0) && (
                    <Alert tone="neutral" title="O que entra na conta" dismissible={false}>
                        {moeda(valorCentavos)} menos {metodo.taxaPercentual}% de taxa ({moeda(taxa)}) ={' '}
                        <strong>{moeda(valorCentavos - taxa)}</strong>
                        {metodo.prazoDias > 0 ? `, liquidados em ${metodo.prazoDias} ${metodo.prazoDias === 1 ? 'dia' : 'dias'}.` : '.'}
                    </Alert>
                )}
            </div>
        </Modal>
    );
}
